import jsPDF from "jspdf";
import "jspdf-autotable";

declare module "jspdf" {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
    lastAutoTable?: {
      finalY: number;
    };
  }
}

interface StrengthTestData {
  reportId: string;
  batchId: string;
  testDate: string;
  testAge: number; // 7, 14, or 28 days
  strength: number;
  cubeWeight: number;
  cubeSize: string;
  load: number;
  grade: string;
  location: string;
  customerName?: string;
  projectName?: string;
  technicianName?: string;
  testingMachine?: string;
}

export function downloadStrengthTestPDF(data: StrengthTestData) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - 2 * margin;

  // Set default values
  const customerName = data.customerName || "ABC Construction Ltd.";
  const projectName = data.projectName || "Metro Tower Phase 2";
  const technicianName = data.technicianName || "Rajesh Kumar";
  const testingMachine = data.testingMachine || "CTM-3000 Compression Testing Machine";

  // Calculate compliance
  const targetStrength = parseInt(data.grade.replace("M", ""));
  let expectedMin = 0;
  if (data.testAge === 7) {
    expectedMin = targetStrength * 0.67; // 67% at 7 days
  } else if (data.testAge === 14) {
    expectedMin = targetStrength * 0.83; // 83% at 14 days
  } else {
    expectedMin = targetStrength; // 100% at 28 days
  }
  const compliance = data.strength >= expectedMin ? "PASS" : "FAIL";
  const complianceColor = compliance === "PASS" ? [15, 61, 46] : [122, 62, 0];

  // Header - NABL Certification
  doc.setFillColor(249, 250, 251);
  doc.rect(0, 0, pageWidth, 35, "F");

  // Logo placeholder (dark indigo box)
  doc.setFillColor(27, 42, 91);
  doc.rect(margin, 8, 15, 15, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("RMC", margin + 7.5, 16.5, { align: "center" });

  // Company name and NABL
  doc.setTextColor(10, 15, 28);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("Ready-Mix Concrete Testing Laboratory", margin + 20, 13);

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(113, 113, 130);
  doc.text("NABL Accredited | ISO 9001:2015 Certified | IS 516:1959 Compliant", margin + 20, 19);

  // Report ID and Date
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(27, 42, 91);
  doc.text(`Report No: ${data.reportId}`, pageWidth - margin, 13, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setTextColor(113, 113, 130);
  doc.text(`Date: ${data.testDate}`, pageWidth - margin, 19, { align: "right" });

  // Title Section
  let yPos = 45;
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(27, 42, 91);
  doc.text(`${data.testAge}-Day Cube Compression Test Report`, pageWidth / 2, yPos, { align: "center" });

  yPos += 3;
  doc.setFillColor(27, 42, 91);
  doc.rect(pageWidth / 2 - 40, yPos, 80, 1, "F");

  // Compliance Badge
  yPos += 10;
  const badgeWidth = 60;
  const badgeHeight = 12;
  doc.setFillColor(complianceColor[0], complianceColor[1], complianceColor[2], 0.08 * 255);
  doc.roundedRect(pageWidth / 2 - badgeWidth / 2, yPos, badgeWidth, badgeHeight, 6, 6, "F");
  doc.setDrawColor(complianceColor[0], complianceColor[1], complianceColor[2]);
  doc.setLineWidth(0.5);
  doc.roundedRect(pageWidth / 2 - badgeWidth / 2, yPos, badgeWidth, badgeHeight, 6, 6, "S");
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(complianceColor[0], complianceColor[1], complianceColor[2]);
  doc.text(compliance, pageWidth / 2, yPos + 8, { align: "center" });

  // Project Information
  yPos += 22;
  doc.autoTable({
    startY: yPos,
    head: [["Project Information"]],
    body: [
      ["Customer Name", customerName],
      ["Project Name", projectName],
      ["Batch ID", data.batchId],
      ["Grade of Concrete", data.grade],
      ["Casting Location", data.location],
    ],
    theme: "plain",
    headStyles: {
      fillColor: [27, 42, 91],
      textColor: [255, 255, 255],
      fontSize: 10,
      fontStyle: "bold",
      cellPadding: 4,
    },
    bodyStyles: {
      fontSize: 9,
      cellPadding: 3,
      textColor: [10, 15, 28],
    },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 60, textColor: [113, 113, 130] },
      1: { cellWidth: contentWidth - 60 },
    },
    margin: { left: margin, right: margin },
  });

  // Test Results
  yPos = (doc as any).lastAutoTable?.finalY + 10 || yPos + 80;
  doc.autoTable({
    startY: yPos,
    head: [["Test Parameters", "Value", "Unit", "Specification"]],
    body: [
      ["Test Age", data.testAge.toString(), "days", `${data.testAge} days`],
      ["Cube Size", data.cubeSize, "", "150 x 150 x 150 mm"],
      ["Cube Weight", data.cubeWeight.toString(), "grams", "8000-8500 g"],
      ["Applied Load", data.load.toString(), "N", "-"],
      ["Cross-sectional Area", "22500", "mm²", "150 x 150 mm"],
      ["Compressive Strength", data.strength.toString(), "MPa", `≥ ${expectedMin.toFixed(1)} MPa`],
    ],
    theme: "striped",
    headStyles: {
      fillColor: [27, 42, 91],
      textColor: [255, 255, 255],
      fontSize: 9,
      fontStyle: "bold",
      cellPadding: 4,
    },
    bodyStyles: {
      fontSize: 9,
      cellPadding: 3,
      textColor: [10, 15, 28],
    },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 60, textColor: [27, 42, 91] },
      1: { cellWidth: 30, halign: "center" },
      2: { cellWidth: 25, halign: "center", textColor: [113, 113, 130] },
      3: { cellWidth: contentWidth - 115, textColor: [113, 113, 130] },
    },
    margin: { left: margin, right: margin },
  });

  // Test Details
  yPos = (doc as any).lastAutoTable?.finalY + 10 || yPos + 80;
  doc.autoTable({
    startY: yPos,
    head: [["Test Execution Details"]],
    body: [
      ["Testing Machine", testingMachine],
      ["Technician Name", technicianName],
      ["Test Standard", "IS 516:1959"],
      ["Curing Method", "Water curing at 27±2°C"],
    ],
    theme: "plain",
    headStyles: {
      fillColor: [249, 250, 251],
      textColor: [27, 42, 91],
      fontSize: 10,
      fontStyle: "bold",
      cellPadding: 4,
    },
    bodyStyles: {
      fontSize: 9,
      cellPadding: 3,
      textColor: [10, 15, 28],
    },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 60, textColor: [113, 113, 130] },
      1: { cellWidth: contentWidth - 60 },
    },
    margin: { left: margin, right: margin },
  });

  // Compliance Summary
  yPos = (doc as any).lastAutoTable?.finalY + 10 || yPos + 60;
  const summaryText = compliance === "PASS" 
    ? `The concrete sample has achieved ${data.strength} MPa compressive strength at ${data.testAge} days, which exceeds the minimum required strength of ${expectedMin.toFixed(1)} MPa for ${data.grade} grade concrete. The test result is COMPLIANT with IS 456:2000 specifications.`
    : `The concrete sample has achieved ${data.strength} MPa compressive strength at ${data.testAge} days, which is below the minimum required strength of ${expectedMin.toFixed(1)} MPa for ${data.grade} grade concrete. The test result is NON-COMPLIANT with IS 456:2000 specifications.`;

  doc.setFillColor(complianceColor[0], complianceColor[1], complianceColor[2], 0.05 * 255);
  doc.roundedRect(margin, yPos, contentWidth, 24, 3, 3, "F");
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(10, 15, 28);
  const splitText = doc.splitTextToSize(summaryText, contentWidth - 8);
  doc.text(splitText, margin + 4, yPos + 5);

  // Footer - Signature Section
  yPos = pageHeight - 40;
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.line(margin, yPos, margin + 60, yPos);
  doc.line(pageWidth - margin - 60, yPos, pageWidth - margin, yPos);

  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(27, 42, 91);
  doc.text("Tested By", margin + 30, yPos + 5, { align: "center" });
  doc.text("Approved By", pageWidth - margin - 30, yPos + 5, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(113, 113, 130);
  doc.text(technicianName, margin + 30, yPos + 10, { align: "center" });
  doc.text("Dr. Amit Sharma", pageWidth - margin - 30, yPos + 10, { align: "center" });
  doc.text("Lab Technician", margin + 30, yPos + 14, { align: "center" });
  doc.text("Quality Manager", pageWidth - margin - 30, yPos + 14, { align: "center" });

  // Footer Note
  doc.setFontSize(7);
  doc.setTextColor(113, 113, 130);
  doc.setFont("helvetica", "italic");
  const footerNote = "This is a computer-generated report. Results relate only to the items tested. Report shall not be reproduced except in full without written approval.";
  const footerLines = doc.splitTextToSize(footerNote, contentWidth);
  doc.text(footerLines, pageWidth / 2, pageHeight - 15, { align: "center" });

  // Page number
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text(`Page 1 of 1`, pageWidth - margin, pageHeight - 8, { align: "right" });

  // Save PDF
  const fileName = `${data.testAge}Day_Strength_Test_${data.batchId}_${data.reportId}.pdf`;
  doc.save(fileName);
}
